/* eslint-disable no-console */
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Skeleton,
  useColorModeValue,
  Link,
  Box,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  UrlAndFilenameGQLType,
  useGetFundingVerificationPipelineRunsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { VerificationStatusCellContent } from './VerificationStatusCellContent';
import { useVerificationOptions } from './VerificationTableSharedComponents';
import { colors } from 'packages/common-frontend/colors';
import { FiDownload } from 'react-icons/fi';

// generates link to download servicer report (input xlsx) that
// was used to kick off the verification pipeline run
export const ServicerReportDownloadButton = ({
  servicerReportDownload,
}: {
  servicerReportDownload: UrlAndFilenameGQLType | null;
}) => {
  if (!servicerReportDownload) {
    return null;
  }
  return (
    <Link
      href={servicerReportDownload.url}
      filename={servicerReportDownload.filename}
      color={colors.info}
      display="flex"
      alignItems="center"
    >
      <FiDownload />
    </Link>
  );
};

// This component lets lender pick previous fileVersion for a given reporting date run.
export const PipelinePicker = ({
  selectedReportingDate,
  selectedFileVersion,
  organizationId,
  servicerReportDownload,
}: {
  organizationId: string;
  selectedFileVersion: number;
  selectedReportingDate: Util.FullDateStr;
  servicerReportDownload: UrlAndFilenameGQLType | null;
}) => {
  const history = useHistory();
  const { navigator } = useVerificationOptions();

  const queryVariables = useMemo(
    () => ({
      fetchPolicy: 'network-only' as const,
      variables: { organizationId },
    }),
    [organizationId],
  ); // Only re-memoize if organizationId changes

  const { data: pipelineRunsData } = useGetFundingVerificationPipelineRunsQuery(
    queryVariables,
  );

  const hoverBg = useColorModeValue('gray.100', 'gray.700');
  const selectedBg = useColorModeValue('blue.100', 'blue.700');

  if (!pipelineRunsData) {
    return <Skeleton height="40px" />;
  }

  return (
    <Box
      borderRadius="8px"
      border={`1px solid ${colors.gray[200]}`}
      backgroundColor="white"
      padding="24px"
    >
      <Table variant="simple" size="md">
        <Thead>
          <Tr bg="gray.50">
            <Th>Version</Th>
            <Th>Upload Date</Th>
            <Th>Uploaded By</Th>
            <Th>Status</Th>
            <Th>Servicer Report</Th>
          </Tr>
        </Thead>
        <Tbody>
          {pipelineRunsData.fundingVerificationPipelineRuns
            .filter(
              ({ reportingDate }) => reportingDate === selectedReportingDate,
            )
            .map(
              ({
                reportingDate,
                creditFacilityId,
                fileVersion,
                status,
                artifactDocumentId,
                initiatorDetails,
              }) => {
                const slashedReportingDate = commonDate.fullDateStrToSlashedDateStr(
                  reportingDate,
                  commonDate.formatSlashedDateStr,
                );

                return (
                  <Tr
                    key={`pipeline-run-${creditFacilityId}-${reportingDate}-${fileVersion}`}
                    bg={
                      selectedFileVersion === fileVersion
                        ? selectedBg
                        : undefined
                    }
                    _hover={{ bg: hoverBg }}
                    cursor="pointer"
                    onClick={() => {
                      navigator(history).setPipeline({
                        artifactDocumentId,
                        creditFacilityId,
                        reportingDate,
                        fileVersion,
                      });
                    }}
                  >
                    <Td>{fileVersion}</Td>
                    <Td>{slashedReportingDate}</Td>
                    <Td>
                      {initiatorDetails
                        ? `${initiatorDetails.firstName} ${initiatorDetails.lastName} (${initiatorDetails?.organizationName})`
                        : '-'}
                    </Td>
                    <Td>
                      <VerificationStatusCellContent pipelineStatus={status} />
                    </Td>
                    {/* <Td>
                      <PipelinePickerWaiverImpactCell
                        artifactDocumentId={artifactDocumentId}
                        creditFacilityId={creditFacilityId}
                        fileVersion={fileVersion}
                        reportingDate={reportingDate}
                      />
                    </Td> */}
                    <Td>
                      <ServicerReportDownloadButton
                        servicerReportDownload={servicerReportDownload}
                      />
                    </Td>
                  </Tr>
                );
              },
            )}
        </Tbody>
      </Table>
    </Box>
  );
};
